<template>
  <ul
    v-if="suggestions.length"
    v-click-outside="resetSuggestions"
    class="popover-menu"
  >
    <li
      v-for="suggestion in suggestions"
      :key="suggestion.placeId"
      class="popover-menu-option"
      tabindex="0"
      @click="setAddress(suggestion.placeId)"
      @keyup.enter="setAddress(suggestion.placeId)"
    >
      <div>
        <i class="fa-solid fa-location-dot popover-menu-option__icon" />
      </div>

      <span class="menu-option__text text-truncate">
        {{ suggestion.text }}
      </span>
    </li>

    <div class="google-logo-container">
      <img
        class="google-logo"
        src="@/assets/images/powered-by-google.png"
        alt="Google logo"
      />
    </div>
  </ul>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { ElNotification, ClickOutside as vClickOutside } from "element-plus";
import { set, debounce } from "lodash-es";
import axios from "axios";

const props = defineProps({
  query: {
    type: String,
    required: true,
  },
  mapsAPIKey: {
    type: String,
    default: "",
  },
});

const emits = defineEmits(["select"]);

// state
const suggestions = ref([]);
const googlePlacesAutocompleteApiUrl =
  "https://places.googleapis.com/v1/places:autocomplete";

// computed
const formesterMapsAPIKey = computed(() => {
  switch (process.env.NODE_ENV) {
    case "development":
      return "AIzaSyBWY-S9hT4SZxuOe1i91Apr16azIgALA-4";
    case "production":
      return "AIzaSyAhfaQxk8zNjI93DCrNg3h4tZFE_-OEt-s";
    default:
      return "AIzaSyBWY-S9hT4SZxuOe1i91Apr16azIgALA-4";
  }
});

// When we want to get API key from the user add the below object to form-initials address settings
// {
//   "type": "general-input",
//   "fieldName": "mapsAPIKey",
//   "title": "Google Maps API Key",
//   "placeholder": "API Key",
//   "hint": "To get your Google Maps API Key, <a href=\"https://developers.google.com/maps/documentation/javascript/get-api-key\" target=\"_blank\">go here</a> and create one, then copy it back in the text box above. Open <a href=\"https://console.developers.google.com/projectselector/apis/credentials\" target=\"_blank\">this link</a> if you already have one. If empty, it will default to our API key.",
//   "visibilityToggle": "autocomplete"
// }
const apiKey = computed(() => props.mapsAPIKey || formesterMapsAPIKey.value);

// methods
const resetSuggestions = () => {
  suggestions.value = [];
};

const setSuggestions = (availableSuggestions) => {
  resetSuggestions();

  suggestions.value = (availableSuggestions || []).map(
    ({ placePrediction }) => {
      return {
        placeId: placePrediction.placeId,
        text: placePrediction.text.text,
      };
    },
  );
};

const fetchPlaces = async (keyword) => {
  if (!keyword.length) {
    resetSuggestions();
    return;
  }

  try {
    const postData = {
      input: keyword,
    };

    const config = {
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": apiKey.value,
      },
    };

    const { data } = await axios.post(
      googlePlacesAutocompleteApiUrl,
      postData,
      config,
    );

    setSuggestions(data.suggestions);
  } catch (error) {
    ElNotification({
      message:
        "Something went wrong! Please check your API key and network connection",
      type: "error",
      position: "bottom-right",
    });
  }
};

const googlePlaceDetailsApiUrl = (placeId) => {
  return `https://places.googleapis.com/v1/places/${placeId}`;
};

const fetchPlaceDetails = async (placeId) => {
  try {
    const config = {
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": apiKey.value,
        "X-Goog-FieldMask": "adrFormatAddress",
      },
    };

    const { data } = await axios.get(googlePlaceDetailsApiUrl(placeId), config);

    return data.adrFormatAddress;
  } catch (error) {
    ElNotification({
      message: "Something went wrong",
      type: "error",
      position: "bottom-right",
    });
  }
};

const formatAddress = (address) => {
  const parser = new DOMParser();
  const parsedDocument = parser.parseFromString(address, "text/html");

  const addressElements = parsedDocument.body.childNodes;
  let streetAddress = "";

  for (let i = 0; i < addressElements.length; i++) {
    const element = addressElements[i];

    if (element.nodeType === Node.TEXT_NODE) {
      streetAddress += element.textContent.trim();
    } else if (
      element.nodeType === Node.ELEMENT_NODE &&
      element.classList.contains("street-address")
    ) {
      if (streetAddress.length) streetAddress += " ";
      streetAddress += element.textContent.trim();
      break;
    }
  }

  // remove any trailing commas
  streetAddress = streetAddress.replace(/,*$/, "");

  const streetAddressLine2 = parsedDocument
    .querySelector(".extended-address")
    ?.textContent.trim();

  const postalCode = parsedDocument
    .querySelector(".postal-code")
    ?.textContent.trim();

  const city = parsedDocument.querySelector(".locality")?.textContent.trim();

  const state = parsedDocument.querySelector(".region")?.textContent.trim();

  const country = parsedDocument
    .querySelector(".country-name")
    ?.textContent.trim();

  return {
    streetAddress,
    streetAddressLine2,
    postalCode,
    city,
    state,
    country,
  };
};

const setAddress = async (placeId) => {
  const rawAddress = await fetchPlaceDetails(placeId);
  const formattedAdress = formatAddress(rawAddress);
  emits("select", formattedAdress);
  resetSuggestions();
};

const fetchPlacesDebounced = debounce(fetchPlaces, 500);

// watch
watch(
  () => props.query,
  () => fetchPlacesDebounced(props.query),
  { immediate: true },
);
</script>

<style scoped>
@import "@/assets/styles/popover.css";

.popover-menu {
  box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.12);
  position: absolute;
  z-index: 999;
  width: 100%;
}

.popover-menu-option__icon {
  color: #b3b3b3;
}

.google-logo-container {
  padding: 16px;
  display: flex;
  justify-content: flex-end;
}

.google-logo {
  height: 18px;
}
</style>
